// STAGING-LAUNCH-READINESS-002-R2-R8-R2 — compact invite: per-card send CTA.
import { useEffect, useRef, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { fetchReferralCabinet } from "./checkout-reservation-v2.js";
import { copyText } from "./contact-manager-v2.js";
import { emitEvent } from "./events-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import {
  buildInviteSharePayload,
  INVITE_MESSAGE_CARDS,
  inviteHowItWorksBody,
  inviteHowItWorksTitle,
  invitePageBody,
  invitePageTitle,
  messageTextWithoutLink,
  openTelegramShareSync,
  type InviteCardId,
} from "./referral-share-v2.js";

export function InviteFriendsV2({
  lang,
  sessionKey,
  onBack,
}: {
  lang: StoreV2Lang;
  sessionKey: string;
  onBack?: () => void;
}) {
  const en = lang === "en";
  const shareGuard = useRef(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [personalLink, setPersonalLink] = useState<string | null>(null);
  const [selected, setSelected] = useState<InviteCardId>("gourmet");
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState<InviteCardId | null>("gourmet");

  useEffect(() => {
    emitEvent("invite_friends_opened", {});
    let cancelled = false;
    void fetchReferralCabinet(sessionKey).then((res) => {
      if (cancelled || !res) return;
      const code =
        res.referral_code && res.referral_code !== "undefined" ? res.referral_code : null;
      const link =
        res.personal_referral_link && !/undefined|null|^$/.test(res.personal_referral_link)
          ? res.personal_referral_link
          : null;
      setReferralCode(code);
      setPersonalLink(link);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  const payloadFor = (cardId: InviteCardId) =>
    buildInviteSharePayload(lang, {
      personalReferralLink: personalLink,
      referralCode,
      cardId,
    });

  const runShare = (cardId: InviteCardId) => {
    const payload = payloadFor(cardId);
    setSelected(cardId);
    emitEvent("referral_share_clicked", {
      surface: "invite_friends",
      has_referral_token: payload.hasReferralToken,
      card: cardId,
    });
    if (shareGuard.current) return;
    shareGuard.current = true;
    window.setTimeout(() => {
      shareGuard.current = false;
    }, 1200);

    emitEvent("referral_share_open_attempted", {
      surface: "invite_friends",
      has_referral_token: payload.hasReferralToken,
      mode: "telegram_share_url",
      card: cardId,
    });
    const result = openTelegramShareSync(payload.shareUrl);
    if (!result.ok) {
      setFallbackOpen(true);
      emitEvent("referral_share_fallback_shown", {
        surface: "invite_friends",
        via: result.via,
        card: cardId,
      });
      emitEvent("referral_share_error", { surface: "invite_friends", via: result.via });
      return;
    }
    setFallbackOpen(false);
    emitEvent("referral_share_sent", {
      surface: "invite_friends",
      has_referral_token: payload.hasReferralToken,
      via: result.via,
      mode: "telegram_share_url",
      card: cardId,
    });
  };

  const runCopy = () => {
    const payload = payloadFor(selected);
    void copyText(payload.previewText).then((ok) => {
      setCopied(ok);
      emitEvent(ok ? "referral_link_copied" : "referral_share_error", {
        surface: "invite_friends",
        mode: "copy_fallback",
        has_referral_token: payload.hasReferralToken,
        card: selected,
      });
    });
  };

  return (
    <div className="sv2-body sv2-route-top" data-route-top="invite-friends" data-testid="sv2-invite-friends">
      <div className="sv2-section-title" data-testid="sv2-invite-honest-title">
        🎁 {invitePageTitle(lang)}
      </div>
      <p className="sv2-muted" data-testid="sv2-invite-honest-body" style={{ marginTop: 8 }}>
        {invitePageBody(lang)}
      </p>
      {personalLink ? (
        <p className="sv2-muted" data-testid="sv2-invite-personal-link" style={{ marginTop: 8, wordBreak: "break-all" }}>
          {personalLink}
        </p>
      ) : null}

      <div className="sv2-invite-cards" data-testid="sv2-invite-cards" style={{ display: "grid", gap: 10, marginTop: 12 }}>
        {INVITE_MESSAGE_CARDS.map((c) => {
          const active = selected === c.id;
          const isOpen = expanded === c.id;
          const preview = messageTextWithoutLink(en ? c.bodyEn : c.bodyUk);
          const full = payloadFor(c.id).previewText;
          return (
            <div
              key={c.id}
              className="sv2-info-card sv2-cabinet-card"
              data-testid={`sv2-invite-card-${c.id}`}
              data-selected={active ? "1" : "0"}
              style={{
                border: active ? "2px solid var(--sv2-accent, #c45c26)" : "1px solid var(--sv2-border, #e5e5e5)",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setSelected(c.id);
                  setExpanded(isOpen ? null : c.id);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span aria-hidden="true" style={{ fontSize: 18, lineHeight: "22px" }}>
                    {active ? "●" : "○"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{en ? c.titleEn : c.titleUk}</strong>
                    <p className="sv2-muted" style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.35 }}>
                      {isOpen ? full : preview}
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                className="sv2-cta"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginTop: 10,
                  width: "100%",
                }}
                data-testid={`sv2-invite-send-${c.id}`}
                onClick={() => runShare(c.id)}
              >
                <StoreV2Icon name="telegram" size={18} />
                <span>{en ? "Send this text" : "Надіслати цей текст"}</span>
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="sv2-cta sv2-secondary"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 12 }}
        data-testid="sv2-invite-copy-link"
        onClick={runCopy}
      >
        <StoreV2Icon name="copy" size={18} />
        <span>
          {copied
            ? en
              ? "Link copied"
              : "Посилання скопійовано"
            : en
              ? "Copy link"
              : "Скопіювати посилання"}
        </span>
      </button>

      {fallbackOpen ? (
        <div
          className="sv2-alert sv2-alert-err"
          style={{ marginTop: 12 }}
          data-testid="sv2-invite-share-fallback"
          role="dialog"
        >
          <strong>{en ? "Could not open the friends list" : "Не вдалося відкрити список друзів"}</strong>
          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="sv2-cta"
              data-testid="sv2-invite-fallback-retry"
              onClick={() => runShare(selected)}
            >
              {en ? "Try again" : "Спробувати ще раз"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              data-testid="sv2-invite-fallback-copy"
              onClick={runCopy}
            >
              {en ? "Copy link" : "Скопіювати посилання"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              data-testid="sv2-invite-fallback-back"
              onClick={() => setFallbackOpen(false)}
            >
              {en ? "Back" : "Назад"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sv2-info-card sv2-cabinet-card" style={{ marginTop: 16 }} data-testid="sv2-invite-how-it-works">
        <div className="sv2-section-title" style={{ fontSize: 15 }}>
          {inviteHowItWorksTitle(lang)}
        </div>
        <p style={{ marginTop: 6, fontSize: 13 }} data-testid="sv2-referral-rewards-disabled">
          {inviteHowItWorksBody(lang)}
        </p>
      </div>

      {onBack ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          style={{ marginTop: 16 }}
          data-testid="sv2-invite-back"
          onClick={onBack}
        >
          {en ? "Back" : "Назад"}
        </button>
      ) : null}
    </div>
  );
}
